import { action, makeObservable, observable } from "mobx";
import { Condition } from "./Conditions/Condition";
import type {
    LLMExecutorStepExecuteStepConfiguration,
    LLMExecutorStepWriteClassifierResultConfiguration,
    LLMExecutorStepWriteToExtendedMetadataConfiguration,
} from "./LLMExecutorStepExecuteStepConfiguration";
import { LLMConstants } from "../constants";

export interface ILLMExecutorStepOutputActionBase {
    conditions: Condition[];
}

interface ILLMExecutorStepOutputActionThenExecuteStep
    extends ILLMExecutorStepOutputActionBase {
    thenActionType: LLMExecutorStepOutputActionType.ExecuteStep;
    thenActionConfiguration: LLMExecutorStepExecuteStepConfiguration;
}

interface ILLMExecutorStepOutputActionThenWriteClassifierResult
    extends ILLMExecutorStepOutputActionBase {
    thenActionType: LLMExecutorStepOutputActionType.WriteClassifierResult;
    thenActionConfiguration: LLMExecutorStepWriteClassifierResultConfiguration;
}

interface ILLMExecutorStepOutputActionThenWriteToExtendedMetadata
    extends ILLMExecutorStepOutputActionBase {
    thenActionType: LLMExecutorStepOutputActionType.WriteExtendedMetadata;
    thenActionConfiguration: LLMExecutorStepWriteToExtendedMetadataConfiguration;
}

export type ILLMExecutorStepOutputAction =
    | ILLMExecutorStepOutputActionThenExecuteStep
    | ILLMExecutorStepOutputActionThenWriteClassifierResult
    | ILLMExecutorStepOutputActionThenWriteToExtendedMetadata;

export enum LLMExecutorStepOutputActionType {
    ExecuteStep = "ExecuteStep",
    WriteClassifierResult = "WriteClassifierResult",
    WriteExtendedMetadata = "WriteExtendedMetadata",
}

interface InputVariable {
    sourceVariable: string;
    targetVariable: string;
}

/**
 * Manages state for both action types which can easily be toggled between. If you use a method that touches data outside
 * of the current action type it is a no-op. An example would be trying to set the step id
 * but the `thenActionType` is `WriteClassifierResult`
 */
export class LLMExecutorStepOutputAction
    implements ILLMExecutorStepOutputActionBase
{
    @observable readonly conditions: Condition[];
    @observable thenActionType: LLMExecutorStepOutputActionType;
    @observable
    thenActionConfiguration: ILLMExecutorStepOutputAction["thenActionConfiguration"];

    /**
     * The way input variables are stored in the `thenActionConfiguration` when `thenActionType` is
     * `ExecuteStep` is not ideal for managing/updating/rendering in the UI. This list is much more managable
     * and essentially propogates it's updates to the configuration variable.
     */
    @observable usableInputVariables: InputVariable[];

    constructor(type?: LLMExecutorStepOutputActionType);
    constructor(action: ILLMExecutorStepOutputAction);
    constructor(
        actionOrType?:
            | LLMExecutorStepOutputActionType
            | ILLMExecutorStepOutputAction,
    ) {
        makeObservable(this);

        this.conditions = [];
        if (typeof actionOrType === "object" && actionOrType !== undefined) {
            const action = actionOrType as ILLMExecutorStepOutputAction;
            Object.assign(this, action);
            this.conditions = action.conditions.map((condition) =>
                Condition.ResolveConcreteCondition(condition),
            );

            if (
                action.thenActionType ===
                LLMExecutorStepOutputActionType.ExecuteStep
            ) {
                this.usableInputVariables = Object.entries(
                    action.thenActionConfiguration["inputVariables"] ?? {},
                ).map(([key, value]) => ({
                    sourceVariable: key,
                    targetVariable: value,
                }));
            } else if (
                action.thenActionType ===
                LLMExecutorStepOutputActionType.WriteClassifierResult
            ) {
                this.thenActionConfiguration["shortWriteFromVariable"] =
                    LLMExecutorStepOutputAction.getShortWriteFromVariable(
                        action.thenActionConfiguration["writeFromVariable"],
                    );
            } else if (
                action.thenActionType ===
                LLMExecutorStepOutputActionType.WriteExtendedMetadata
            ) {
                this.thenActionConfiguration["shortWriteFromVariable"] =
                    LLMExecutorStepOutputAction.getShortWriteFromVariable(
                        action.thenActionConfiguration["writeFromVariable"],
                    );
            }
        } else {
            this.setThenActionType(
                actionOrType ?? LLMExecutorStepOutputActionType.ExecuteStep,
            );
        }
    }

    private static getShortWriteFromVariable(writeFromVariable: string) {
        const [prefix, ...rest] = writeFromVariable.split(".");

        if (prefix === LLMConstants.StaticValueVariablePrefix) {
            return rest.join(".");
        }

        // If it's not static, we only take the end because some variables are of the form __amd.ml_models.Contact Type
        return rest[rest.length - 1];
    }

    @action
    public addCondition(condition: Condition) {
        this.conditions.push(condition);
    }

    @action
    public removeCondition(condition: Condition) {
        const index = this.conditions.indexOf(condition);
        if (index < 0) return false;
        this.conditions.splice(index, 1);
        return true;
    }

    @action
    public setThenActionType(type: LLMExecutorStepOutputActionType) {
        this.thenActionType = type;

        switch (type) {
            case LLMExecutorStepOutputActionType.ExecuteStep:
                this.thenActionConfiguration = {
                    stepId: "",
                    inputVariables: {},
                };
                this.usableInputVariables = [];
                break;
            case LLMExecutorStepOutputActionType.WriteClassifierResult:
                this.thenActionConfiguration = {
                    classifierId: "",
                    writeFromVariable: "",
                    shortWriteFromVariable: "",
                };
                break;
            case LLMExecutorStepOutputActionType.WriteExtendedMetadata:
                this.thenActionConfiguration = {
                    extendedMetadataFieldName: "",
                    extendedMetadataClassifierId: "",
                    writeFromVariable: "",
                    shortWriteFromVariable: "",
                    allowOverwrite: false,
                };
                break;
        }
    }

    public isExecuteStep() {
        return (
            this.thenActionType === LLMExecutorStepOutputActionType.ExecuteStep
        );
    }
    public isWriteClassifierResult() {
        return (
            this.thenActionType ===
            LLMExecutorStepOutputActionType.WriteClassifierResult
        );
    }
    public isWriteToExtendedMetadata() {
        return (
            this.thenActionType ===
            LLMExecutorStepOutputActionType.WriteExtendedMetadata
        );
    }

    @action
    public setClassifierId(classifierId: string) {
        if (!this.isWriteClassifierResult()) return;
        this.thenActionConfiguration["classifierId"] = classifierId;
    }

    @action
    public setWriteFromVariable(variable: string) {
        if (
            !this.isWriteClassifierResult() &&
            !this.isWriteToExtendedMetadata()
        )
            return;
        this.thenActionConfiguration["writeFromVariable"] = variable;
    }

    @action
    public setShortWriteFromVariable(variable: string) {
        if (
            !this.isWriteClassifierResult() &&
            !this.isWriteToExtendedMetadata()
        )
            return;
        this.thenActionConfiguration["shortWriteFromVariable"] = variable;
    }

    /**
     * @deprecated use `setExtendedMetadataClassifierId` instead
     * @param field
     * @returns
     */
    @action
    public setExtendedMetadataField(field: string) {
        if (!this.isWriteToExtendedMetadata()) return;
        this.thenActionConfiguration["extendedMetadataFieldName"] = field;
    }

    @action
    public setExtendedMetadataClassifierId(classifierId: string) {
        if (!this.isWriteToExtendedMetadata()) return;
        this.thenActionConfiguration["extendedMetadataClassifierId"] =
            classifierId;
        // clear out old/obsolete value
        this.thenActionConfiguration["extendedMetadataFieldName"] = "";
    }

    @action
    public setStepId(stepId: string) {
        if (!this.isExecuteStep()) return;
        this.thenActionConfiguration["stepId"] = stepId;
    }

    @action
    public setInputVariableTarget(
        inputVariable: InputVariable,
        targetVariable?: string,
    ) {
        if (!this.isExecuteStep()) return;

        const inputVariables = this.thenActionConfiguration[
            "inputVariables"
        ] as Record<string, string>;

        if (targetVariable === undefined) {
            delete inputVariables[inputVariable.sourceVariable];
            this.usableInputVariables.splice(
                this.usableInputVariables.indexOf(inputVariable),
                1,
            );
        } else {
            inputVariable.targetVariable = targetVariable;
            inputVariables[inputVariable.sourceVariable] = targetVariable;
        }
    }

    @action
    public setInputVariableSource(
        inputVariable: InputVariable,
        sourceVariable: string,
    ) {
        if (!this.isExecuteStep()) return;

        const inputVariables = this.thenActionConfiguration["inputVariables"];

        // copy the value at the old key to the new key
        inputVariables[sourceVariable] =
            inputVariables[inputVariable.sourceVariable];
        // delete the old key
        delete inputVariables[inputVariable.sourceVariable];

        inputVariable.sourceVariable = sourceVariable;
    }

    @action
    public addInputVariable() {
        if (!this.isExecuteStep()) return;

        this.usableInputVariables.push({
            sourceVariable: "",
            targetVariable: "",
        });
        // won't propogate this change to the configuration since an empty variable does nothing,
        // once it's updated it will be propogated.
    }
}
